/**
 * Activate Service Modal
 *
 * Allows activating customer services when a job is completed
 * Captures service-specific metadata (kit numbers, serials, etc.)
 */

import { useState } from 'react';
import { X, Wifi, Save } from 'lucide-react';
import { Job } from '../types/schema';
import { CustomerService } from '../types/schema';
import { createCustomerService } from '../services/customer-service.service';

interface ActivateServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job | null;
}

export function ActivateServiceModal({ isOpen, onClose, job }: ActivateServiceModalProps) {
  const [serviceType, setServiceType] = useState<CustomerService['serviceType']>('Starlink');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Starlink metadata
  const [kitNumber, setKitNumber] = useState('');
  const [starlinkAccountNumber, setStarlinkAccountNumber] = useState('');
  const [starlinkModel, setStarlinkModel] = useState<'Standard' | 'Mini' | ''>('');

  // 4G/5G metadata
  const [carrier, setCarrier] = useState<'Telstra' | 'Optus' | ''>('');
  const [simNumber, setSimNumber] = useState('');

  // NBN metadata
  const [nbnServiceId, setNbnServiceId] = useState('');
  const [nbnLocationId, setNbnLocationId] = useState('');
  const [technology, setTechnology] = useState('');

  // OptiComm metadata (private fibre wholesaler — FTTP only, no tech tiers)
  const [opticommServiceId, setOpticommServiceId] = useState('');
  const [opticommOrderNumber, setOpticommOrderNumber] = useState('');

  // WiFi Extension metadata
  const [apCount, setApCount] = useState('');
  const [apSerials, setApSerials] = useState('');

  // Smart Home metadata
  const [hubSerial, setHubSerial] = useState('');
  const [deviceCount, setDeviceCount] = useState('');

  // Security metadata
  const [panelSerial, setPanelSerial] = useState('');
  const [cameraCount, setCameraCount] = useState('');
  const [sensorCount, setSensorCount] = useState('');

  // VoIP metadata
  const [sasbossCustomerName, setSasbossCustomerName] = useState('');
  const [mainNumber, setMainNumber] = useState('');
  const [handsetCount, setHandsetCount] = useState('');
  const [handsetLocations, setHandsetLocations] = useState('');
  const [portingRequired, setPortingRequired] = useState(false);
  const [numbersToPort, setNumbersToPort] = useState('');
  const [portingStatus, setPortingStatus] = useState<'' | 'pending' | 'submitted' | 'approved' | 'completed'>('');
  const [callFlowNotes, setCallFlowNotes] = useState('');

  // Common metadata
  const [accountNumber, setAccountNumber] = useState('');
  const [planName, setPlanName] = useState('');
  const [monthlyFee, setMonthlyFee] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [notes, setNotes] = useState('');

  const resetForm = () => {
    setKitNumber('');
    setStarlinkAccountNumber('');
    setStarlinkModel('');
    setCarrier('');
    setSimNumber('');
    setNbnServiceId('');
    setNbnLocationId('');
    setTechnology('');
    setOpticommServiceId('');
    setOpticommOrderNumber('');
    setApCount('');
    setApSerials('');
    setHubSerial('');
    setDeviceCount('');
    setPanelSerial('');
    setCameraCount('');
    setSensorCount('');
    setSasbossCustomerName('');
    setMainNumber('');
    setHandsetCount('');
    setHandsetLocations('');
    setPortingRequired(false);
    setNumbersToPort('');
    setPortingStatus('');
    setCallFlowNotes('');
    setAccountNumber('');
    setPlanName('');
    setMonthlyFee('');
    setContractEndDate('');
    setNotes('');
  };

  const handleActivateService = async () => {
    if (!job || !job.customerId) {
      alert('Job must be linked to a customer');
      return;
    }

    setIsSubmitting(true);

    try {
      // Build metadata based on service type
      const metadata: CustomerService['metadata'] = {};

      // Service-specific metadata
      if (serviceType === 'Starlink') {
        if (kitNumber) metadata.kitNumber = kitNumber;
        if (starlinkAccountNumber) metadata.starlinkAccountNumber = starlinkAccountNumber.trim();
        if (starlinkModel) metadata.starlinkModel = starlinkModel;
      } else if (serviceType === '4G/5G') {
        if (carrier) metadata.carrier = carrier;
        if (simNumber) metadata.simNumber = simNumber;
      } else if (serviceType === 'NBN') {
        if (nbnServiceId) metadata.nbnServiceId = nbnServiceId;
        if (nbnLocationId) metadata.nbnLocationId = nbnLocationId;
        if (technology) metadata.technology = technology;
      } else if (serviceType === 'OptiComm') {
        if (opticommServiceId) metadata.opticommServiceId = opticommServiceId.trim();
        if (opticommOrderNumber) metadata.opticommOrderNumber = opticommOrderNumber.trim();
      } else if (serviceType === 'WiFi Extension') {
        if (apCount) metadata.accessPointCount = parseInt(apCount);
        if (apSerials) metadata.accessPointSerials = apSerials.split(',').map((s) => s.trim());
      } else if (serviceType === 'Smart Home') {
        if (hubSerial) metadata.hubSerialNumber = hubSerial;
        if (deviceCount) metadata.deviceCount = parseInt(deviceCount);
      } else if (serviceType === 'Security') {
        if (panelSerial) metadata.panelSerialNumber = panelSerial;
        if (cameraCount) metadata.cameraCount = parseInt(cameraCount);
        if (sensorCount) metadata.sensorCount = parseInt(sensorCount);
      } else if (serviceType === 'VoIP') {
        if (sasbossCustomerName) metadata.sasbossCustomerName = sasbossCustomerName;
        if (mainNumber) metadata.mainNumber = mainNumber;
        if (handsetCount) metadata.handsetCount = parseInt(handsetCount);
        if (handsetLocations) metadata.handsetLocations = handsetLocations;
        metadata.portingRequired = portingRequired;
        if (portingRequired && numbersToPort.trim()) {
          metadata.numbersToPort = numbersToPort
            .split(/[,\n]/)
            .map((n) => n.trim())
            .filter(Boolean);
        }
        if (portingStatus) metadata.portingStatus = portingStatus;
        if (callFlowNotes) metadata.callFlowNotes = callFlowNotes;
      }

      // Common metadata
      if (accountNumber) metadata.accountNumber = accountNumber;
      if (planName) metadata.planName = planName;
      if (monthlyFee) metadata.monthlyFee = parseFloat(monthlyFee);
      if (contractEndDate) metadata.contractEndDate = contractEndDate;

      // Create the service
      await createCustomerService(
        job.customerId,
        job.client,
        serviceType,
        {
          jobId: job.id,
          metadata,
          address: job.address,
          notes: notes || undefined,
          installationNotes: `Installed via job: ${job.type} - ${job.loc}`,
        }
      );

      alert(`${serviceType} service activated successfully!`);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error activating service:', error);
      alert('Failed to activate service. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !job) return null;

  // Get available services from the job
  const jobServices = job.services || [];

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-secondary to-yellow-600 flex items-center justify-center shadow-lg">
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Activate Service</h2>
              <p className="text-sm text-slate-400">{job.client}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Service Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Service Type *
            </label>
            {jobServices.length > 0 && (
              <p className="text-xs text-slate-400 mb-2">
                Job services: {jobServices.join(', ')}
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(['NBN', 'OptiComm', 'Starlink', '4G/5G', 'VoIP', 'WiFi Extension', 'Smart Home', 'Security', 'IT'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setServiceType(type)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    serviceType === type
                      ? 'bg-brand-secondary text-white shadow-lg scale-105'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20'
                  } ${jobServices.includes(type) ? 'border-2 border-brand-secondary/50' : ''}`}
                >
                  {type}
                </button>
              ))}
            </div>
            {/* "Other" demoted to a smaller secondary chip */}
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setServiceType('Other')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  serviceType === 'Other'
                    ? 'bg-slate-500/60 text-white border border-slate-400'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'
                }`}
              >
                Other…
              </button>
            </div>
          </div>

          {/* Starlink Metadata */}
          {serviceType === 'Starlink' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Starlink Details</h3>

              {/* Model Selection — 2 toggle buttons */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Starlink Model
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setStarlinkModel('Standard')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      starlinkModel === 'Standard'
                        ? 'bg-indigo-600 text-white shadow-lg border-2 border-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Standard (Gen 3)
                  </button>
                  <button
                    type="button"
                    onClick={() => setStarlinkModel('Mini')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      starlinkModel === 'Mini'
                        ? 'bg-indigo-600 text-white shadow-lg border-2 border-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Starlink Mini
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Kit Number *
                </label>
                <input
                  type="text"
                  value={kitNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKitNumber(e.target.value)}
                  placeholder="e.g., UTxxxxxxxxxxxxxx-xxxxxxx"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>

              {/* Starlink Account Number */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Starlink Account Number
                </label>
                <input
                  type="text"
                  value={starlinkAccountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStarlinkAccountNumber(e.target.value)}
                  placeholder="ACC-XXXXXXX-XXXXX-XX"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Searchable from /services and global search. Also auto-populated by Starlink billing emails.
                </p>
              </div>
            </div>
          )}

          {/* 4G/5G Metadata */}
          {serviceType === '4G/5G' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">4G/5G Details</h3>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Carrier *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCarrier('Telstra')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      carrier === 'Telstra'
                        ? 'bg-blue-600 text-white shadow-lg border-2 border-blue-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Telstra
                  </button>
                  <button
                    type="button"
                    onClick={() => setCarrier('Optus')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      carrier === 'Optus'
                        ? 'bg-teal-600 text-white shadow-lg border-2 border-teal-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Optus
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  SIM Number *
                </label>
                <input
                  type="text"
                  value={simNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSimNumber(e.target.value)}
                  placeholder="Enter SIM number"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
            </div>
          )}

          {/* NBN Metadata */}
          {serviceType === 'NBN' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">NBN Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    NBN Service ID
                  </label>
                  <input
                    type="text"
                    value={nbnServiceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNbnServiceId(e.target.value)}
                    placeholder="Service ID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    NBN Location ID
                  </label>
                  <input
                    type="text"
                    value={nbnLocationId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNbnLocationId(e.target.value)}
                    placeholder="Location ID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Technology Type
                </label>
                <select
                  value={technology}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTechnology(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                >
                  <option value="">Select technology</option>
                  <option value="FTTP">FTTP (Fiber to the Premises)</option>
                  <option value="FTTC">FTTC (Fiber to the Curb)</option>
                  <option value="FTTN">FTTN (Fiber to the Node)</option>
                  <option value="FTTB">FTTB (Fiber to the Building)</option>
                  <option value="HFC">HFC (Hybrid Fiber Coaxial)</option>
                  <option value="Fixed Wireless">Fixed Wireless</option>
                  <option value="Satellite">Satellite</option>
                </select>
              </div>
            </div>
          )}

          {/* OptiComm Metadata */}
          {serviceType === 'OptiComm' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">OptiComm Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    OptiComm Service ID
                  </label>
                  <input
                    type="text"
                    value={opticommServiceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpticommServiceId(e.target.value)}
                    placeholder="Service / NID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    OptiComm Order Number
                  </label>
                  <input
                    type="text"
                    value={opticommOrderNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpticommOrderNumber(e.target.value)}
                    placeholder="Order number"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* WiFi Extension Metadata */}
          {serviceType === 'WiFi Extension' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">WiFi Extension Details</h3>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Number of Access Points
                </label>
                <input
                  type="number"
                  value={apCount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApCount(e.target.value)}
                  placeholder="e.g., 3"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Access Point Serials (comma-separated)
                </label>
                <input
                  type="text"
                  value={apSerials}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApSerials(e.target.value)}
                  placeholder="e.g., AP001, AP002, AP003"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
            </div>
          )}

          {/* Smart Home Metadata */}
          {serviceType === 'Smart Home' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Smart Home Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Hub Serial Number
                  </label>
                  <input
                    type="text"
                    value={hubSerial}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubSerial(e.target.value)}
                    placeholder="Hub serial"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Number of Devices
                  </label>
                  <input
                    type="number"
                    value={deviceCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeviceCount(e.target.value)}
                    placeholder="e.g., 10"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* VoIP Metadata */}
          {serviceType === 'VoIP' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">VoIP Details</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Sasboss Customer Name
                  </label>
                  <input
                    type="text"
                    value={sasbossCustomerName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSasbossCustomerName(e.target.value)}
                    placeholder="Customer name in Sasboss portal"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Main Business Number
                  </label>
                  <input
                    type="text"
                    value={mainNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMainNumber(e.target.value)}
                    placeholder="e.g. 02 4000 0000"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Handsets Installed
                  </label>
                  <input
                    type="number"
                    value={handsetCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHandsetCount(e.target.value)}
                    placeholder="e.g. 4"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Handset Locations
                  </label>
                  <input
                    type="text"
                    value={handsetLocations}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHandsetLocations(e.target.value)}
                    placeholder="e.g. Reception x2, office x2"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={portingRequired}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPortingRequired(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
                  />
                  Number Porting Required
                </label>
                {portingRequired && (
                  <div className="space-y-3 pl-6">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Numbers to Port</label>
                      <textarea
                        value={numbersToPort}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNumbersToPort(e.target.value)}
                        placeholder="02 4000 0000, 02 4000 0001"
                        rows={2}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Porting Status</label>
                      <select
                        value={portingStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPortingStatus(e.target.value as '' | 'pending' | 'submitted' | 'approved' | 'completed')}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                      >
                        <option value="">Not started</option>
                        <option value="pending">Pending</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Call Flow Notes
                </label>
                <textarea
                  value={callFlowNotes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCallFlowNotes(e.target.value)}
                  placeholder="Queues, ring groups, auto-attendant, after-hours routing..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                />
              </div>
            </div>
          )}

          {/* Security Metadata */}
          {serviceType === 'Security' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Security System Details</h3>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Panel Serial Number
                </label>
                <input
                  type="text"
                  value={panelSerial}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPanelSerial(e.target.value)}
                  placeholder="Panel serial"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Number of Cameras
                  </label>
                  <input
                    type="number"
                    value={cameraCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCameraCount(e.target.value)}
                    placeholder="e.g., 4"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Number of Sensors
                  </label>
                  <input
                    type="number"
                    value={sensorCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSensorCount(e.target.value)}
                    placeholder="e.g., 8"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Common Metadata */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
            <h3 className="text-lg font-bold text-white">Service Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountNumber(e.target.value)}
                  placeholder="Account #"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={planName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlanName(e.target.value)}
                  placeholder="e.g., Standard 100"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Monthly Fee ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={monthlyFee}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMonthlyFee(e.target.value)}
                  placeholder="e.g., 139.00"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Contract End Date
                </label>
                <input
                  type="date"
                  value={contractEndDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContractEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Service Notes
              </label>
              <textarea
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                placeholder="Any additional notes about this service..."
                rows={3}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleActivateService}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-secondary to-yellow-600 hover:from-brand-secondary/90 hover:to-yellow-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Activating...' : 'Activate Service'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
